import React, { useState, useRef } from 'react';
import { NodeService } from '../../services/NodeService';
import { auth } from '../../lib/firebase';

const XMarkIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
const DocumentTextIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
const ArrowUpTrayIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>;

const InstructionMarkdownDialog = ({ open, onClose, parentId, onSaved }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setContent(event.target.result);
            if (!title) {
                // Auto-set title from filename (remove .md)
                setTitle(file.name.replace(/\.[^/.]+$/, ""));
            }
        };
        reader.readAsText(file);
    };

    const handleSave = async () => {
        if (!title.trim() || !content.trim()) return;
        setIsSaving(true);
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("No user logged in");

            // 1. Create the Instruction Node
            const settings = {
                type: 'instruction',
                content: content.trim()
            };
            const newNodeId = await NodeService.createNode(user.uid, title.trim(), parentId, settings);

            // 2. Link it to the parent Space's instructionFileIds
            const parentNode = await NodeService.getNode(parentId);
            if (parentNode) {
                const currentIds = parentNode.settings?.instructionFileIds || [];
                const newIds = Array.from(new Set([...currentIds, newNodeId]));
                const newSettings = {
                    ...parentNode.settings,
                    instructionFileIds: newIds
                };
                await NodeService.updateNodeSettings(parentId, newSettings);
            }

            if (onSaved) onSaved(newNodeId);
            onClose();
            // Reset
            setTitle('');
            setContent('');
        } catch (err) {
            console.error("Failed to save instruction document:", err);
            alert("Failed to save instruction document.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-md transition-opacity" onClick={onClose} />

            <div className="relative w-full max-w-2xl bg-[var(--bg-secondary)] border border-[var(--border-strong)] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="px-10 py-8 flex items-center justify-between border-b border-[var(--border-subtle)]">
                    <div>
                        <h2 className="text-2xl font-semibold text-[var(--text-main)] tracking-tight flex items-center gap-3">
                            <DocumentTextIcon className="w-6 h-6 text-indigo-500" />
                            New AI Instruction
                        </h2>
                        <p className="text-[13px] text-[var(--text-muted)] mt-1">
                            Paste or upload a markdown document to guide the AI.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] transition-all">
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-10 py-8 space-y-6 custom-scrollbar">
                    {/* Title Input */}
                    <div className="space-y-3">
                        <label className="block text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest ml-1">Document Title</label>
                        <input
                            autoFocus
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Coding Standards or Project Context"
                            className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-strong)] text-[var(--text-main)] text-[14px] rounded-2xl px-5 py-3.5 outline-none focus:border-[var(--text-muted)]/30 transition-all"
                        />
                    </div>

                    {/* Content Area */}
                    <div className="space-y-3 flex-1 flex flex-col">
                        <div className="flex items-center justify-between ml-1">
                            <label className="block text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest">Markdown Content</label>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="text-[10px] font-bold text-indigo-500 hover:text-indigo-400 uppercase tracking-widest flex items-center gap-1.5 transition-colors"
                            >
                                <ArrowUpTrayIcon className="w-3 h-3" />
                                Upload File
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept=".md,.txt"
                                onChange={handleFileUpload}
                            />
                        </div>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="# Your Instructions here...
Provide specific rules, examples, or context for the AI."
                            className="w-full h-80 bg-[var(--bg-tertiary)] border border-[var(--border-strong)] text-[var(--text-main)] text-[14px] rounded-3xl px-6 py-5 outline-none focus:border-[var(--text-muted)]/30 transition-all resize-none font-mono leading-relaxed"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-10 py-8 border-t border-[var(--border-subtle)] flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--text-main)] transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || !title.trim() || !content.trim()}
                        className="px-10 py-3.5 bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold rounded-2xl transition-all shadow-xl shadow-indigo-500/10 flex items-center gap-2"
                    >
                        {isSaving ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                        ) : null}
                        Save Instruction
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InstructionMarkdownDialog;
